import { useCallback, useMemo, type ReactNode } from "react";
import type { Spec } from "vega";
import { fitToHost, type HostSize } from "../custom/fitToHost.js";
import { radarChartSpec, type RadarChartSpecOptions } from "../specs/radar.js";
import { buildVegaRuntimeConfig } from "../theme/config.js";
import { useVizTheme } from "../theme/useVizTheme.js";
import type { ChartData } from "../types.js";
import { EmbedFrame, useSafeEmbedOptions } from "./EmbedFrame.js";
import { registerVizExpressions } from "./expressions.js";
import type { ChartContainerProps } from "./shared.js";
import { hostSizeOf } from "./VegaChart.js";

registerVizExpressions();

export interface RadarChartProps
  extends RadarChartSpecOptions, ChartContainerProps {
  data: ChartData;
}

/**
 * A radar spec sized to its host. Its marks are placed from the centre within
 * the width and height (see `radarChartSpec`), so it takes the host's size
 * as given — no autosizing, which would chase its own bounds.
 */
export function fitRadarToHost(spec: Spec, size: HostSize): Spec {
  return {
    ...fitToHost(spec, size),
    autosize: { type: "none", contains: "padding" },
    padding: 4,
  };
}

const DEFAULT_HEIGHT = 300;

/** A themed radar chart: one value per category, around a centre. */
// Its complexity is its prop list (each spec option and the container's,
// with defaults); the logic lives in radarChartSpec and hostSizeOf, tested.
// fallow-ignore-next-line complexity
export function RadarChart({
  data,
  category,
  value,
  categoryTitle,
  valueTitle,
  grid,
  filled,
  color,
  labels,
  valueFormat,
  title,
  subtitle,
  height = DEFAULT_HEIGHT,
  width = "container",
  containerWidth,
  detail,
  renderer = "svg",
  actions = false,
  tooltip = true,
  theme,
  className,
  style,
  onNewView,
  onError,
  errorFallback,
}: RadarChartProps): ReactNode {
  const { palette, isDark } = useVizTheme(theme);
  const themePalette = theme?.palette;
  const font = theme?.font;
  const config = useMemo(
    () => buildVegaRuntimeConfig(palette, { palette: themePalette, font }),
    [palette, themePalette, font],
  );
  const spec = useMemo(
    () =>
      fitRadarToHost(
        radarChartSpec({
          data,
          category,
          value,
          categoryTitle,
          valueTitle,
          grid,
          filled,
          color,
          labels,
          valueFormat,
          title,
          subtitle,
        }),
        hostSizeOf({ height, width, containerWidth, detail }),
      ),
    [
      data,
      category,
      value,
      categoryTitle,
      valueTitle,
      grid,
      filled,
      color,
      labels,
      valueFormat,
      title,
      subtitle,
      height,
      width,
      containerWidth,
      detail,
    ],
  );
  const options = useSafeEmbedOptions({
    mode: "vega",
    renderer,
    actions,
    tooltip,
    isDark,
    config,
  });
  const fallback = useCallback(() => errorFallback, [errorFallback]);

  return (
    <EmbedFrame
      className={className}
      style={style}
      spec={spec}
      options={options}
      onNewView={onNewView}
      onError={onError}
      errorFallback={errorFallback === undefined ? undefined : fallback}
      trackWidth={width === "container"}
    />
  );
}
